import { useState, useEffect, useCallback } from 'react';
import { 
    collection, 
    query, 
    limit, 
    onSnapshot, 
    setDoc, 
    doc, 
    deleteDoc 
} from 'firebase/firestore';
import { db, DB_PATH } from '../config/firebase';

/**
 * Hook customizado para leitura e escrita no Firestore em tempo real utilizando tenanting (DB_PATH)
 * @param {string} collectionName Nome da coleção (ex: 'users', 'departments')
 * @param {boolean} authReady Flag indicando se a autenticação já está pronta
 * @param {number} limitCount Limite de registros a serem buscados (0 para ilimitado)
 * @returns {object} { data, isLoading, saveItem, deleteItem }
 */
export const useFirestoreData = (collectionName, authReady, limitCount = 0) => {
    const [data, setData] = useState([]);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        if (!db || !authReady) return;
        
        // Regra do Tenant: usamos DB_PATH + collectionName
        const fullPath = DB_PATH + collectionName;
        let q = query(collection(db, fullPath));
        
        if (limitCount > 0) {
            q = query(collection(db, fullPath), limit(limitCount));
        }

        const unsubscribe = onSnapshot(q, (snapshot) => {
            setData(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
            setIsLoading(false);
        }, (error) => {
            console.error(`Erro ao ler coleção ${collectionName}:`, error);
            setIsLoading(false);
        });

        return () => unsubscribe();
    }, [authReady, collectionName, limitCount]);

    // Função para salvar ou atualizar um item
    const saveItem = useCallback(async (item) => {
        if (!db) return;
        const itemToSave = JSON.parse(JSON.stringify(item));
        const id = String(itemToSave.id || Date.now());
        delete itemToSave.id;
        
        const fullPath = DB_PATH + collectionName;
        await setDoc(doc(db, fullPath, id), itemToSave);
    }, [collectionName]);

    // Função para deletar um item
    const deleteItem = useCallback(async (id) => {
        if (!db) return;
        const fullPath = DB_PATH + collectionName;
        await deleteDoc(doc(db, fullPath, id));
    }, [collectionName]);

    return { data, isLoading, saveItem, deleteItem };
};
